import { useState, useCallback } from 'react';
import { useSearch } from './useSearch';
import { usePopular } from './usePopular';
import { useFilter } from './useFilter';

export function useMovieAndTV() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [randomLoading, setRandomLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showTrailerOverlay, setShowTrailerOverlay] = useState(false);
  const [trailerURL, setTrailerURL] = useState(null);
  
  // Track discovery mode
  const [isRandomMode, setIsRandomMode] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all'); // 'all', 'movie', 'tv', 'search'

  const {
    searchQuery,
    setSearchQuery,
    suggestions,
    setSuggestions,
    fetchSuggestions,
    errorMessage,
    setErrorMessage,
    handleSearch: handleSearchFromHook,
  } = useSearch();

  const {
    handlePopularMovies: handlePopularMoviesFromHook,
    handlePopularTVShows: handlePopularTVShowsFromHook,
  } = usePopular();

  const {
    ratingFilter,
    setRatingFilter,
    decadeFilter,
    setDecadeFilter,
    genreFilter,
    setGenreFilter,
    applyFilters,
  } = useFilter();

  const handleSearchWrapper = useCallback(async (overrideQuery) => {
    setIsRandomMode(false);
    setActiveCategory('search');
    setSuggestions([]);
    await handleSearchFromHook(setResults, setLoading, overrideQuery);
  }, [handleSearchFromHook, setSuggestions]);

  const resetSearch = useCallback(() => {
    setSearchQuery('');
    setSuggestions([]);
    setResults([]);
    setErrorMessage('');
    setIsRandomMode(false);
    setActiveCategory('all');
    setLoading(false);
    setRandomLoading(false);
  }, [setSearchQuery, setSuggestions, setErrorMessage]);

  const handleRandomMovies = useCallback(async () => {
    setSearchQuery('');
    setSuggestions([]);
    setErrorMessage('');
    setIsRandomMode(true);
    setActiveCategory('movie');
    setRandomLoading(true);

    try {
      const movies = await handlePopularMoviesFromHook(applyFilters, (preliminary) => {
        if (preliminary && preliminary.length > 0) {
          setResults(preliminary);
          setRandomLoading(false);
        }
      });
      if (movies && movies.length > 0) {
        setResults(movies);
      } else {
        setErrorMessage('Geen films gevonden. Probeer je filters te resetten.');
      }
    } catch (error) {
      console.error('Error fetching random movies:', error);
      setErrorMessage('Kon geen films ophalen.');
    } finally {
      setRandomLoading(false);
    }
  }, [handlePopularMoviesFromHook, applyFilters, setSearchQuery, setSuggestions, setErrorMessage]);

  const handleRandomTVShows = useCallback(async () => {
    setSearchQuery('');
    setSuggestions([]);
    setErrorMessage('');
    setIsRandomMode(true);
    setActiveCategory('tv');
    setRandomLoading(true);

    try {
      const tvShows = await handlePopularTVShowsFromHook(applyFilters, (preliminary) => {
        if (preliminary && preliminary.length > 0) {
          setResults(preliminary);
          setRandomLoading(false);
        }
      });
      if (tvShows && tvShows.length > 0) {
        setResults(tvShows);
      } else {
        setErrorMessage('Geen TV shows gevonden. Probeer je filters te resetten.');
      }
    } catch (error) {
      console.error('Error fetching random TV shows:', error);
      setErrorMessage('Kon geen TV shows ophalen.');
    } finally {
      setRandomLoading(false);
    }
  }, [handlePopularTVShowsFromHook, applyFilters, setSearchQuery, setSuggestions, setErrorMessage]);

  const openTrailerOverlay = useCallback((itemOrKey) => {
    if (!itemOrKey) return;
    if (typeof itemOrKey === 'string') {
      setTrailerURL(`https://www.youtube.com/watch?v=${itemOrKey}`);
      setSelectedItem(null);
    } else {
      setSelectedItem(itemOrKey);
      if (itemOrKey.trailer) {
        setTrailerURL(
          itemOrKey.trailer.includes('http')
            ? itemOrKey.trailer
            : `https://www.youtube.com/watch?v=${itemOrKey.trailer}`
        );
      } else {
        setTrailerURL(null);
      }
    }
    setShowTrailerOverlay(true);
  }, []);

  const closeTrailerOverlay = useCallback(() => {
    setShowTrailerOverlay(false);
    setTrailerURL(null);
    setSelectedItem(null);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    suggestions,
    fetchSuggestions,
    results,
    setResults,
    randomLoading,
    loading,
    setLoading,
    selectedItem,
    showTrailerOverlay,
    setShowTrailerOverlay,
    trailerURL,
    setTrailerURL,
    errorMessage,
    setErrorMessage,
    handleSearch: handleSearchWrapper,
    handlePopularMovies: handleRandomMovies,
    handlePopularTVShows: handleRandomTVShows,
    openTrailerOverlay,
    closeTrailerOverlay,
    ratingFilter,
    setRatingFilter,
    decadeFilter,
    setDecadeFilter,
    genreFilter,
    setGenreFilter,
    isRandomMode,
    activeCategory,
    resetSearch
  };
}